import type { Reducer } from 'react';
import { useReducer, useRef, useEffect } from 'react';
import type { Observable, Subscription } from 'rxjs';

type ObservableStatus = 'NOT_STARTED' | 'OBSERVING' | 'COMPLETED' | 'FAILED' | 'STOPPED';

interface ObservableNotStarted {
  status: 'NOT_STARTED';
  data?: undefined;
  error?: any;
  withLoader: boolean;
}
interface ObservableSubscribed<D> {
  status: 'OBSERVING';
  data?: D;
  error?: any;
  withLoader: boolean;
}
interface ObservableCompleted<D> {
  status: 'COMPLETED';
  data: D;
  error?: undefined;
  withLoader: boolean;
}
interface ObservableFailed<D> {
  status: 'FAILED';
  data?: D;
  error: any;
  withLoader: boolean;
}
interface ObservableStopped<D> {
  status: 'STOPPED';
  data?: D;
  error?: undefined;
  withLoader: boolean;
}
type ObservableState<D> =
  | ObservableNotStarted
  | ObservableSubscribed<D>
  | ObservableCompleted<D>
  | ObservableFailed<D>
  | ObservableStopped<D>;

interface SubscribeAction {
  type: 'SUBSCRIBE';
}
interface AddLoaderAction {
  type: 'ADD_LOADER';
}
interface SetDataAction<D> {
  type: 'SET_DATA';
  value: D;
}
interface SetErrorAction {
  type: 'SET_ERROR';
  error: any;
}
interface CompleteAction {
  type: 'COMPLETE';
}
interface UnsubscribeAction {
  type: 'UNSUBSCRIBE';
}
type ObservableAction<D> =
  | SubscribeAction
  | AddLoaderAction
  | SetDataAction<D>
  | SetErrorAction
  | CompleteAction
  | UnsubscribeAction;

type ObservableReducer<D> = Reducer<ObservableState<D>, ObservableAction<D>>;

interface UseObservableOptions<D> {
  lazy?: boolean;
  onComplete?: (data: D) => void;
  onError?: (error: any) => void;
  id?: string; // we can use this param to debug
}

type UseObservableHook<D> = ObservableState<D> & {
  restart: () => void;
  stop: () => void;
};

const initialState: ObservableNotStarted = { status: 'NOT_STARTED', withLoader: false };

function reducer<D>(state: ObservableState<D>, action: ObservableAction<D>): ObservableState<D> {
  switch (action.type) {
    case 'SUBSCRIBE':
      return {
        ...state,
        status: 'OBSERVING'
      };
    case 'ADD_LOADER':
      return {
        ...state,
        withLoader: true
      };
    case 'SET_DATA':
      return {
        ...state,
        status: 'OBSERVING',
        data: action.value,
        error: undefined,
        withLoader: false
      };
    case 'SET_ERROR':
      return {
        ...state,
        status: 'FAILED',
        error: action.error,
        withLoader: false
      };
    case 'COMPLETE':
      // when subscription is completed, subscription must have some data
      // if it happened, the problem come from observable which should not complete without data
      return {
        ...state,
        status: 'COMPLETED',
        error: undefined,
        withLoader: false
      } as ObservableCompleted<D>;
    case 'UNSUBSCRIBE':
      return {
        ...state,
        status: 'STOPPED',
        error: undefined,
        withLoader: false
      };
  }
}

function useObservable<D>(
  observable: Observable<D>,
  options: UseObservableOptions<D> = {}
): UseObservableHook<D> {
  const [state, dispatch] = useReducer<ObservableReducer<D>>(reducer, initialState);
  const loaderTimeout = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;
  const handleCompleteRef = useRef(options.onComplete);
  const handleErrorRef = useRef(options.onError);
  handleCompleteRef.current = options.onComplete;
  handleErrorRef.current = options.onError;
  const handleCancelRef = useRef<() => void>(() => undefined);
  const handleRestartRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    loaderTimeout.current = window.setTimeout(() => {
      dispatch({ type: 'ADD_LOADER' });
      loaderTimeout.current = 0;
    }, 0);
  }, [observable]);

  useEffect(() => {
    const cancelLoaderTimeout = () => {
      if (loaderTimeout.current) {
        window.clearTimeout(loaderTimeout.current);
        loaderTimeout.current = 0;
      }
    };

    let sub: Subscription | undefined;
    const subscribe = () => {
      dispatch({ type: 'SUBSCRIBE' });
      sub = observable.subscribe({
        next: value => {
          cancelLoaderTimeout();
          dispatch({ type: 'SET_DATA', value });
        },
        error: error => {
          cancelLoaderTimeout();
          if (handleErrorRef.current) {
            handleErrorRef.current(error);
          }
          console.error(error);
          dispatch({ type: 'SET_ERROR', error });
        },
        complete: () => {
          cancelLoaderTimeout();
          if (handleCompleteRef.current) {
            handleCompleteRef.current(stateRef.current.data!);
          }
          dispatch({ type: 'COMPLETE' });
        }
      });
    };
    const unsubscribe = () => {
      dispatch({ type: 'UNSUBSCRIBE' });
      if (sub && !sub.closed) {
        sub.unsubscribe();
      }
    };

    if (!options.lazy) {
      subscribe();
    }

    handleCancelRef.current = unsubscribe;
    handleRestartRef.current = () => {
      if (sub) {
        sub.unsubscribe();
      }
      subscribe();
    };

    return () => {
      if (sub) {
        sub.unsubscribe();
      }
    };
  }, [observable]);

  return {
    ...state,
    stop: handleCancelRef.current,
    restart: handleRestartRef.current
  };
}

export type { ObservableStatus };
export { useObservable };
